import React, { useState } from 'react'
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import { FetchDataComponent } from './data/FetchDataComponent'
import { useSelector } from 'react-redux'
import { State } from './types/state'
import { AxiosConfigContainer } from './requests/AxiosConfigContainer'
import { ToastProvider } from './toast/Toast'
import LoadingSpinnerProvider from './loading-spinner/LoadingSpinner'
import LandingPage from './components/Landing/LandingPage'
import Footer from './components/Footer/Footer'
import Contact from './components/Contact/Contact'
import UserProfile from './components/User/UserProfile'
import DesignersList from './components/Designers/DesignersList'
import DesignerDetails from './components/Designers/DesignerDetails'
import Messages from './components/Messages/Messages'
import ProjectsList from './components/Projects/ProjectsList'
import ProjectDetails from './components/Projects/ProjectDetails'
import DesignerProfileEdit from './components/Designers/DesignerProfileEdit'
import PlantSearch from './components/Plants/PlantSearch'
import PlantDetails from './components/Plants/PlantDetails'
import TaskBoardsList from './components/Tasks/TaskBoardsList'
import TaskBoard from './components/Tasks/TaskBoard'
import Blog from './components/Blog/Blog'
import BlogDetails from './components/Blog/BlogDetails'
import BrandZone from './components/Brands/BrandZone'
import BrandDetails from './components/Brands/BrandDetails'
import NurseryDetails from './components/Nurseries/NurseryDetails'
import Pricing from './components/Pricing/Pricing'
import FAQ from './components/FAQ/FAQ'
import OrderSummary from './components/Pricing/OrderSummary'
import PlantForm from './components/Plants/PlantForm'
import AddBrandForm from './components/Brands/AddBrandForm'
import EditBrandForm from './components/Brands/EditBrandForm'
import BlogPostForm from './components/Blog/BlogPostForm'
import NurseryList from './components/Nurseries/NurseryList'
import NurseryForm from './components/Nurseries/NurseryForm'
import SharedProjectView from './components/Projects/SharedProjectView'
import { Package } from './enums/package.enum'
import { isMinimumPackage } from './utils/package-validator'
import NotFound from './components/NotFound/NotFound'
import ProductsZone from './components/Brands/ProductsZone'
import ProductDetails from './components/Brands/ProductDetails'
import ProductForm from './components/Brands/ProductForm'
import ProductStats from './components/Brands/ProductStats'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ActivateAccountPage from './pages/ActivateAccountPage'
import Cooperation from './components/Cooperation/Cooperation'
import AdvancedPlantSearch from './components/Plants/AdvancedPlantSearch'
import ChangePassword from './components/User/ChangePassword'
import ForgotPassword from './login/ForgotPassword'
import Features from './components/Features/Features'
import KnowledgeBase from './components/KnowledgeBase/KnowledgeBase'

const App = (): JSX.Element => {
  const { username, isSuperadmin, packageExpiredAt, package: packageInfo, packageUsersNumber } = useSelector((state: State) => state.user)

  return (
    <>
      <BrowserRouter>
        <AxiosConfigContainer />
        <ToastProvider />
        <LoadingSpinnerProvider />
        <FetchDataComponent />
        <Router username={username} isSuperadmin={isSuperadmin} packageExpiredAt={packageExpiredAt} package={packageInfo} packageUsersNumber={packageUsersNumber} />
      </BrowserRouter>
    </>
  )
}

interface RouterProps {
  username: string | null
  isSuperadmin: boolean | undefined
  packageExpiredAt: Date | null
  package: Package | null
  packageUsersNumber: number | null
}

const Router = ({ username, isSuperadmin, packageExpiredAt, package: packageInfo }: RouterProps): JSX.Element => {
  return (
    <div className='wrapper'>
      <link rel='stylesheet' href='https://use.fontawesome.com/releases/v5.15.1/css/all.css' />
      <link
        rel='stylesheet'
        href='https://use.fontawesome.com/releases/v5.15.1/css/all.css'
      />
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/login' element={<LoginPage />} />
        <Route path='/register' element={<RegisterPage />} />
        <Route path='/activate-account' element={<ActivateAccountPage />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/contact' element={<Contact />} />
        <Route path='/features' element={<Features />} />
        <Route path='/knowledge-base' element={<KnowledgeBase />} />
        <Route path='/pricing' element={<Pricing showHeader showComparisonTable />} />
        <Route path='/faq' element={<FAQ />} />
        {username && <Route path='/order-summary' element={<OrderSummary />} />}
        {username && <Route path='/user-profile' element={<UserProfile />} />}
        {username && <Route path='/change-password' element={<ChangePassword />} />}
        <Route path='/designers' element={<DesignersList />} />
        <Route path='/designers/:id' element={<DesignerDetails />} />
        {isMinimumPackage(packageInfo, Package.START, packageExpiredAt) && <Route path='/designer/edit' element={<DesignerProfileEdit />} />}
        {isMinimumPackage(packageInfo, Package.START, packageExpiredAt) && <Route path='/designer/edit/:id' element={<DesignerProfileEdit />} />}
        {username && <Route path='/messages' element={<Messages />} />}
        <Route path='/projects' element={<ProjectsList />} />
        <Route path='/projects/:id' element={<ProjectDetails />} />
        <Route path='/projects/shared/:shareId' element={<SharedProjectView />} />
        {isSuperadmin && <Route path='/plants' element={<PlantSearch />} />}
        {isSuperadmin && <Route path='/plants/advanced-search' element={<AdvancedPlantSearch />} />}
        {isSuperadmin && <Route path='/plants/:id' element={<PlantDetails />} />}
        {isSuperadmin && <Route path='/plants/add' element={<PlantForm />} />}
        {isSuperadmin && <Route path='/plants/edit/:id' element={<PlantForm />} />}
        <Route path='/brands' element={<BrandZone />} />
        {isSuperadmin && <Route path='/brands/add' element={<AddBrandForm />} />}
        {isSuperadmin && <Route path='/brands/edit/:id' element={<EditBrandForm />} />}
        <Route path='/brands/:id' element={<BrandDetails />} />
        {isSuperadmin && <Route path='/products/add' element={<ProductForm />} />}
        {isSuperadmin && <Route path='/products/:id/edit' element={<ProductForm />} />}
        <Route path='/tasks' element={<TaskBoardsList />} />
        <Route path='/tasks/:id' element={<TaskBoard />} />
        <Route path='/blog' element={<Blog />} />
        <Route path='/blog/:id' element={<BlogDetails />} />
        {isSuperadmin && <Route path='/blog/add' element={<BlogPostForm />} />}
        {isSuperadmin && <Route path='/blog/edit/:id' element={<BlogPostForm />} />}
        {isMinimumPackage(packageInfo, Package.PLUS, packageExpiredAt) && <Route path='/nurseries' element={<NurseryList />} />}
        {isSuperadmin && <Route path='/nurseries/new' element={<NurseryForm />} />}
        {isSuperadmin && <Route path='/nurseries/edit/:id' element={<NurseryForm />} />}
        {isMinimumPackage(packageInfo, Package.PLUS, packageExpiredAt) && <Route path='/nurseries/:id' element={<NurseryDetails />} />}
        <Route path='/products' element={<ProductsZone />} />
        <Route path='/products/:id' element={<ProductDetails />} />
        {isSuperadmin && <Route path='/product-stats' element={<ProductStats />} />}
        <Route path='/cooperation' element={<Cooperation />} />
        <Route path='*' element={<NotFound />} />
      </Routes>
    </div>
  )
}

export default App
